import React, { createContext, useContext, useEffect, useState } from 'react';

import { Button, useMediaQuery, CssBaseline } from '@mui/material';
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { deepmerge } from '@mui/utils';

import { Config } from './App';
import { showTokenModal } from './components/admin/TokenModal';
import defaultTheme, { themeOptions } from './theme';
import { getMainDomain } from './utils/component-utils';
import { initializeSiteFonts } from './utils/fontUtils';
import CircularProgress from '@mui/material/CircularProgress';

interface ConfigContextProps {
  config: Config | null;
  isLoading: boolean;
  handleUpload: (blob: Blob, filename: string) => Promise<boolean>;
}

const ConfigContext = createContext<ConfigContextProps | undefined>(undefined);

const hostname = window.location.hostname;

export const useConfig = (): ConfigContextProps => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

export const handleUpload = async (
  blob: Blob,
  filename: string
): Promise<boolean> => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost') {
    // Save the file locally using the File System Access API
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error('Error saving file locally:', error);
      return false;
    }
  } else {
    // Try to get auth token from localStorage first
    let authToken = localStorage.getItem('authToken');
    let uploadSuccess = false;

    // If no token in storage or upload fails, try with modal
    if (!authToken) {
      authToken = await showTokenModal();
      if (!authToken) {
        return false;
      }
    }

    // Upload the file to the server
    const mainDomain = getMainDomain();
    const uploadUrl = `https://upload.${mainDomain}/upload/${hostname}`;

    // Create a FormData instance
    const formData = new FormData();
    formData.append('file', blob, `${filename}`);

    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        body: formData,
        mode: 'cors',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        // Check specific error status codes
        if (response.status === 403) {
          // Token might be invalid, request a new one
          const newToken = await showTokenModal();
          if (!newToken) {
            return false;
          }

          // Try upload again with new token
          authToken = newToken;
          const retryResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: formData,
            mode: 'cors',
            credentials: 'include',
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });

          if (!retryResponse.ok) {
            throw new Error(`HTTP error! status: ${retryResponse.status}`);
          }
          uploadSuccess = true;
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } else {
        uploadSuccess = true;
      }

      if (uploadSuccess) {
        // Save the valid token to localStorage
        localStorage.setItem('authToken', authToken);

        return true;
      }

      return false;
    } catch (error) {
      console.error('Error uploading file:', error);
      // Show user-friendly error message
      alert(error);
      return false;
    }
  }
};

export const ConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [configName, setConfigName] = useState('');
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

  useEffect(() => {
    const fetchConfig = async () => {
      setIsLoading(true);
      const hostname = window.location.hostname;
      let configUrl = `./site_data/${hostname}/${hostname}.json`;

      setConfigName(hostname);
      if (hostname === 'localhost' || hostname === '192.168.10.189') {
        configUrl = `./site_data/localhost/localhost.json`;
      } else {
        configUrl = `/site_data/${hostname}/${hostname}.json`;
      }

      try {
        let data;
        if (process.env.NODE_ENV === 'development') {
          // Read the config file directly from the local file system
          const response = await fetch(configUrl);
          if (!response.ok) {
            data = null;
          } else {
            data = await response.json();
          }
        } else {
          // Fetch the config file from the server
          const response = await fetch(configUrl);
          if (!response.ok) {
            data = null;
          } else {
            data = await response.json();
          }
        }

        // Validate the configuration
        if (!data || !data.pages || !data.pages.Home || !data.footerConfig) {
          console.error('Invalid configuration:', data);
          data = null;
        }

        if (data && !data.theme) {
          data.theme = defaultTheme;
        }

        setConfig(data);
      } catch (error) {
        console.error('Error fetching config:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, []);

  // Initialize fonts after config is set
  useEffect(() => {
    if (config) {
      initializeSiteFonts(config);
    }
  }, [config]);

  const handleNewConfig = async () => {
    const newConfig: Config = {
      theme: defaultTheme,
      pages: {
        Home: {
          menutitle: 'Home',
          components: [],
        },
      },
      navigationConfig: {},
      footerConfig: {},
    };

    const blob = new Blob([JSON.stringify(newConfig, null, 2)], {
      type: 'application/json',
    });
    await handleUpload(blob, `${configName}.json`);
  };

  if (isLoading) {
    return (
      <div>
        <CircularProgress />
      </div>
    );
  }

  if (!config || !config.pages || !config.pages.Home || !config.footerConfig) {
    return (
      <div>
        <Button variant="contained" color="primary" onClick={handleNewConfig}>
          Create New Config
        </Button>
      </div>
    );
  }

  // Correct approach - set mode during theme creation
  // First create the merged theme object
  // Merge the config theme with the default theme options, not the created theme object
  let mergedTheme = deepmerge(themeOptions, {
    ...config.theme,
    palette: {
      ...config.theme?.palette,
      mode: config.theme?.palette?.mode || (prefersDarkMode ? 'dark' : 'light'),
    },
  });

  // Create the theme first with merged properties
  let theme = createTheme(mergedTheme);

  return (
    <ConfigContext.Provider value={{ config, isLoading, handleUpload }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ConfigContext.Provider>
  );
};
